import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReviewItem } from '@/constants/core';

type ReviewsContextValue = {
  reviews: ReviewItem[] | null;
  loading: boolean;
  error?: string;
  total?: number;
  avg?: number;
  fetchedAt?: string;
};

type ReviewsPayload = {
  reviews: ReviewItem[];
  total?: number;
  avg?: number;
  fetchedAt?: string;
};

let cachedPayload: ReviewsPayload | undefined = undefined;
let inflight: Promise<ReviewsPayload> | null = null;

function computeAvg(arr: ReviewItem[]): number | undefined {
  if (!arr.length) return undefined;
  const sum = arr.reduce((s, r) => s + (Number(r.rating) || 0), 0);
  return Number((sum / arr.length).toFixed(2));
}

function scheduleAfterFirstPaint(callback: () => void) {
  let rafId: number | undefined;
  let idleId: number | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let cancelled = false;

  const run = () => {
    if (cancelled) return;
    callback();
  };

  const scheduleIdle = () => {
    if (cancelled) return;

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
      return;
    }

    timeoutId = globalThis.setTimeout(run, 1200);
  };

  if (
    typeof window !== 'undefined' &&
    typeof window.requestAnimationFrame === 'function'
  ) {
    rafId = window.requestAnimationFrame(scheduleIdle);
  } else {
    timeoutId = globalThis.setTimeout(run, 0);
  }

  return () => {
    cancelled = true;
    if (rafId != null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(rafId);
    }
    if (idleId != null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
    if (timeoutId != null) {
      globalThis.clearTimeout(timeoutId);
    }
  };
}

async function fetchReviewsOnce(): Promise<ReviewsPayload> {
  if (cachedPayload) return cachedPayload;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const injected = (globalThis as any).__SITE_REVIEWS__ as
        | { reviews: ReviewItem[]; total?: number; fetchedAt?: string }
        | ReviewItem[]
        | undefined;

      if (injected) {
        const arr = Array.isArray(injected)
          ? injected
          : Array.isArray(injected.reviews)
            ? injected.reviews
            : [];

        const total = Array.isArray(injected) ? undefined : injected.total;
        const fetchedAt = Array.isArray(injected)
          ? undefined
          : injected.fetchedAt;

        cachedPayload = {
          reviews: arr,
          total,
          avg: computeAvg(arr),
          fetchedAt,
        };
        return cachedPayload;
      }

      const res = await fetch('/data/google-reviews.json');

      if (!res.ok) {
        cachedPayload = { reviews: [] };
        return cachedPayload;
      }

      const j = await res.json();

      const total =
        typeof j?.user_ratings_total === 'number'
          ? j.user_ratings_total
          : undefined;
      const fetchedAt =
        typeof j?.fetched_at === 'string' ? j.fetched_at : undefined;

      const arr: ReviewItem[] = Array.isArray(j?.reviews)
        ? j.reviews.map((r: any) => ({
            author: r.author_name,
            text: r.text,
            translations: r.translations,
            rating: Number(r.rating) || 0,
            dateISO: r.time_iso,
            source: 'site' as const,
            profileUrl: undefined,
          }))
        : [];

      cachedPayload = { reviews: arr, total, avg: computeAvg(arr), fetchedAt };
      return cachedPayload;
    } catch {
      cachedPayload = { reviews: [] };
      return cachedPayload;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

const ReviewsContext = createContext<ReviewsContextValue>({
  reviews: null,
  loading: false,
});

export function ReviewsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ReviewsContextValue>({
    reviews: cachedPayload?.reviews ?? null,
    loading: cachedPayload === undefined,
    total: cachedPayload?.total,
    avg: cachedPayload?.avg,
    fetchedAt: cachedPayload?.fetchedAt,
  });

  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    if (cachedPayload !== undefined) {
      setState({
        reviews: cachedPayload.reviews,
        loading: false,
        total: cachedPayload.total,
        avg: cachedPayload.avg,
        fetchedAt: cachedPayload.fetchedAt,
      });

      return () => {
        mounted.current = false;
      };
    }

    const cancelScheduledFetch = scheduleAfterFirstPaint(() => {
      if (!mounted.current) return;

      setState(s => ({ ...s, loading: true }));

      fetchReviewsOnce()
        .then(payload => {
          if (!mounted.current) return;
          setState({
            reviews: payload.reviews,
            loading: false,
            total: payload.total,
            avg: payload.avg,
            fetchedAt: payload.fetchedAt,
          });
        })
        .catch(() => {
          if (!mounted.current) return;
          setState({
            reviews: [],
            loading: false,
            error: 'Failed loading reviews',
          });
        });
    });

    return () => {
      mounted.current = false;
      cancelScheduledFetch();
    };
  }, []);

  const value = useMemo(() => state, [state]);

  return (
    <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>
  );
}

export function useSiteReviews() {
  return useContext(ReviewsContext);
}
